import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Chip,
  IconButton,
  Alert,
  TextField,
  MenuItem,
  Checkbox,
  Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  CalendarMonth as CalendarIcon,
  DeleteSweep as DeleteSweepIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useShowsApi } from "@/context/AppContext";
import type { ShowList as ShowListType } from "api-client/models";
import { formatDateForDisplay } from "@/utils/dateUtils";

const STATUS_COLORS: Record<
  string,
  "default" | "info" | "success" | "warning" | "error"
> = {
  tentative: "default",
  confirmed: "info",
  completed: "success",
  settled: "success",
  cancelled: "error",
};

const STATUS_LABELS: Record<string, string> = {
  tentative: "Tentative",
  confirmed: "Confirmed",
  completed: "Completed",
  settled: "Settled",
  cancelled: "Cancelled",
};

export const ShowList: React.FC = () => {
  const [shows, setShows] = useState<ShowListType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedShows, setSelectedShows] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const showsApi = useShowsApi();

  useEffect(() => {
    fetchShows();

    // Check for navigation state message
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      setInfoMessage(state.message);
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location, statusFilter]);

  const fetchShows = async () => {
    try {
      setLoading(true);
      const response = await showsApi.showsList({
        // Status filtering not yet in OpenAPI schema - will be added later
        // For now we'll filter client-side
      });

      const showsData = response.results || [];
      setShows(showsData);
      setError(null);
    } catch {
      setError("Failed to load shows. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (showId: string) => {
    if (!confirm("Are you sure you want to delete this show?")) {
      return;
    }

    try {
      await showsApi.showsDestroy({ id: showId });
      fetchShows();
    } catch {
      alert("Failed to delete show. Please try again.");
    }
  };

  const handleSelectAll = () => {
    if (selectedShows.size === filteredShows.length) {
      setSelectedShows(new Set());
    } else {
      setSelectedShows(new Set(filteredShows.map((show) => show.id)));
    }
  };

  const handleSelectShow = (showId: string) => {
    const newSelected = new Set(selectedShows);
    if (newSelected.has(showId)) {
      newSelected.delete(showId);
    } else {
      newSelected.add(showId);
    }
    setSelectedShows(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedShows.size === 0) return;

    if (
      !confirm(
        `Are you sure you want to delete ${selectedShows.size} show${selectedShows.size !== 1 ? "s" : ""}?`,
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      // Delete all selected shows
      await Promise.all(
        Array.from(selectedShows).map((showId) =>
          showsApi.showsDestroy({ id: showId }),
        ),
      );

      setInfoMessage(
        `Successfully deleted ${selectedShows.size} show${selectedShows.size !== 1 ? "s" : ""}`,
      );
      setSelectedShows(new Set());
      await fetchShows();
    } catch {
      setError("Failed to delete some shows. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Use utility function for consistent date formatting
  const formatDate = (date: Date | string) => {
    return formatDateForDisplay(date, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  };

  const filteredShows = shows.filter((show) => {
    // Filter by search query
    if (
      searchQuery &&
      !show.title.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    // Filter by status
    if (statusFilter !== "all" && show.status !== statusFilter) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading shows...</Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink
          component="button"
          onClick={() => navigate("/")}
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          underline="hover"
          color="inherit"
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </MuiLink>
        <Typography
          color="text.primary"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <EventIcon sx={{ mr: 0.5 }} fontSize="small" />
          Shows
        </Typography>
      </Breadcrumbs>

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Shows
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your performance schedule and show details
          </Typography>
        </Box>
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CalendarIcon />}
            onClick={() => navigate("/shows/calendar")}
          >
            Calendar View
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/shows/new")}
          >
            New Show
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {infoMessage && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          onClose={() => setInfoMessage(null)}
        >
          {infoMessage}
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3}>
        <TextField
          label="Search shows"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flexGrow: 1 }}
        />
        <TextField
          select
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="tentative">Tentative</MenuItem>
          <MenuItem value="confirmed">Confirmed</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="settled">Settled</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
      </Stack>

      {/* Bulk Actions Bar */}
      {selectedShows.size > 0 && (
        <Alert
          severity="info"
          sx={{
            mb: 3,
            flexWrap: { xs: "wrap", sm: "nowrap" },
            "& .MuiAlert-action": {
              width: { xs: "100%", sm: "auto" },
              ml: { xs: 0, sm: "auto" },
              pl: { xs: 0, sm: 2 },
              pt: { xs: 1, sm: 0 },
            },
          }}
          action={
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
              <Button
                size="small"
                color="inherit"
                onClick={() => setSelectedShows(new Set())}
              >
                Clear
              </Button>
              <Button
                size="small"
                color="error"
                variant="contained"
                startIcon={<DeleteSweepIcon />}
                onClick={handleBulkDelete}
                disabled={isDeleting}
              >
                Delete {selectedShows.size}
              </Button>
            </Stack>
          }
        >
          {selectedShows.size} show{selectedShows.size !== 1 ? "s" : ""}{" "}
          selected
        </Alert>
      )}

      {/* Select All Checkbox */}
      {filteredShows.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Checkbox
            checked={
              selectedShows.size === filteredShows.length &&
              filteredShows.length > 0
            }
            indeterminate={
              selectedShows.size > 0 &&
              selectedShows.size < filteredShows.length
            }
            onChange={handleSelectAll}
          />
          <Typography component="span" variant="body2" color="text.secondary">
            Select all {filteredShows.length} show
            {filteredShows.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
      )}

      {filteredShows.length === 0 ? (
        <Card>
          <CardContent>
            <Stack alignItems="center" spacing={2} py={4}>
              <EventIcon sx={{ fontSize: 64, color: "text.secondary" }} />
              <Typography variant="h6" color="text.secondary">
                {searchQuery || statusFilter !== "all"
                  ? "No shows found matching your filters"
                  : "No shows yet"}
              </Typography>
              {!searchQuery && statusFilter === "all" && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/shows/new")}
                >
                  Create Your First Show
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {filteredShows.map((show) => (
            <Card key={show.id}>
              <CardContent>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "44px minmax(0, 1fr)",
                      sm: "44px minmax(0, 1fr) auto",
                    },
                    alignItems: "start",
                    gap: 1,
                  }}
                >
                  {/* Checkbox for selection */}
                  <Checkbox
                    checked={selectedShows.has(show.id)}
                    onChange={() => handleSelectShow(show.id)}
                    sx={{ mt: 0.5 }}
                  />

                  <Stack spacing={2} sx={{ minWidth: 0 }}>
                    <Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        onClick={() => navigate(`/shows/${show.id}`)}
                        sx={{
                          cursor: "pointer",
                          overflowWrap: "anywhere",
                          "&:hover": {
                            textDecoration: "underline",
                            color: "readable.primary",
                          },
                          transition: "color 0.2s",
                        }}
                      >
                        {show.title}
                      </Typography>
                      <Stack
                        direction="row"
                        sx={{ flexWrap: "wrap", gap: 1 }}
                        mb={1}
                      >
                        <Chip
                          label={STATUS_LABELS[show.status] || show.status}
                          color={STATUS_COLORS[show.status] || "default"}
                          size="small"
                        />
                        {show.isSettled && (
                          <Chip
                            label="Settled"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </Box>

                    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {show.showDate ? (
                            <>
                              {formatDate(show.showDate)}
                              {show.showTime && ` at ${show.showTime}`}
                            </>
                          ) : (
                            "Tentative - Hold dates pending confirmation"
                          )}
                        </Typography>
                      </Stack>

                      {/* Only show revenue for completed or settled shows */}
                      {(show.status === "completed" ||
                        show.status === "settled") && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <MoneyIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Revenue: {formatCurrency(show.totalRevenue)}
                          </Typography>
                        </Stack>
                      )}

                      {show.attendance !== null &&
                        show.attendance !== undefined &&
                        (show.status === "completed" ||
                          show.status === "settled") && (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Attendance: {show.attendance}
                            </Typography>
                          </Stack>
                        )}
                    </Stack>
                  </Stack>

                  <Stack
                    direction="row"
                    sx={{
                      gridColumn: { xs: 2, sm: "auto" },
                      flexWrap: "wrap",
                      gap: 1,
                      "& > button": { minWidth: 44, minHeight: 44 },
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => navigate(`/shows/${show.id}`)}
                    >
                      View
                    </Button>
                    <IconButton
                      size="small"
                      aria-label={`Edit ${show.title}`}
                      onClick={() => navigate(`/shows/${show.id}/edit`)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`Delete ${show.title}`}
                      onClick={() => handleDelete(show.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};
